/**
 * Firm Copilot - AI assistant with cross-family visibility for MFO principals
 */

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { useToast } from "@/hooks/use-toast";
import {
  Loader2,
  Send,
  Plus,
  Folder,
  FolderPlus,
  MessageSquare,
  MoreVertical,
  Pencil,
  Trash2,
  ChevronRight,
  ChevronDown,
  User,
  Building2,
  Sparkles,
  Copy,
  Check,
} from "lucide-react";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { generateThreadTitle, getToolLabel } from "@/lib/copilot/utils";

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
}

interface CopilotFolder {
  id: string;
  name: string;
  color: string | null;
  created_at: string;
}

interface Thread {
  id: string;
  title: string;
  folder_id: string | null;
  created_at: string;
}

const STARTER_QUESTIONS = [
  "What's our total AUM across all families?",
  "Which GPs do we have the largest total commitment to?",
  "Show me funds where multiple families are invested",
  "What's the operational health status of the firm?",
  "Compare performance across all families",
  "Which families might be interested in deal sharing opportunities?",
];

const FOLDER_COLORS = [
  { name: 'Default', value: null },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Red', value: '#ef4444' },
];


export default function FirmCopilot() {
  const { orgId, isMFO, isLoading: tenantLoading, currentOrg } = useTenant();
  const { toast } = useToast();

  // State
  const [folders, setFolders] = useState<CopilotFolder[]>([]);
  const [threads, setThreads] = useState<Thread[]>([]);
  const [currentThread, setCurrentThread] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [toolStatus, setToolStatus] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Folder UI state
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState<CopilotFolder | null>(null);
  const [folderName, setFolderName] = useState("");
  const [folderColor, setFolderColor] = useState<string | null>(null);

  // Load folders and threads - MUST be before any conditional returns (React hooks rules)
  useEffect(() => {
    if (orgId && isMFO) {
      loadFolders();
      loadThreads();
    }
  }, [orgId, isMFO]);

  // Load messages when thread changes
  useEffect(() => {
    if (currentThread) {
      loadMessages(currentThread);
    }
  }, [currentThread]);

  // Auto-scroll on new messages
  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadFolders = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || !orgId) return;

    const { data, error } = await supabase
      .from('firm_copilot_folders')
      .select('*')
      .eq('org_id', orgId)
      .eq('user_id', user.id)
      .order('name');

    if (!error && data) {
      setFolders(data);
      setExpandedFolders(new Set(data.map(f => f.id)));
    }
  };

  const loadThreads = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || !orgId) return;

    const { data, error } = await supabase
      .from('firm_copilot_threads')
      .select('id, title, folder_id, created_at')
      .eq('org_id', orgId)
      .eq('created_by', user.id)
      .order('created_at', { ascending: false })
      .limit(50);

    if (!error && data) {
      setThreads(data);
    }
  };

  const loadMessages = async (threadId: string) => {
    const { data, error } = await supabase
      .from('firm_copilot_messages')
      .select('*')
      .eq('thread_id', threadId)
      .order('created_at');

    if (!error && data) {
      setMessages(data as Message[]);
    }
  };

  // Group threads by folder - MUST be before any conditional returns (React hooks rules)
  const groupedThreads = useMemo(() => {
    const unfoldered = threads.filter(t => !t.folder_id);
    const foldered: Record<string, Thread[]> = {};
    folders.forEach(folder => {
      foldered[folder.id] = threads.filter(t => t.folder_id === folder.id);
    });
    return { unfoldered, foldered };
  }, [threads, folders]);

  // Redirect SFO users (AFTER all hooks)
  if (!tenantLoading && !isMFO) {
    return <Navigate to="/dashboard" replace />;
  }

  // Folder CRUD operations
  const createFolder = async () => {
    if (!folderName.trim() || !orgId) return;

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('firm_copilot_folders')
      .insert({
        org_id: orgId,
        user_id: user.id,
        name: folderName.trim(),
        color: folderColor
      })
      .select()
      .single();

    if (!error && data) {
      setFolders([...folders, data]);
      setExpandedFolders(new Set([...expandedFolders, data.id]));
      toast({ title: "Folder created" });
    } else {
      toast({ title: "Error", description: "Failed to create folder", variant: "destructive" });
    }

    setFolderDialogOpen(false);
    setFolderName("");
    setFolderColor(null);
  };

  const updateFolder = async () => {
    if (!editingFolder || !folderName.trim()) return;

    const { error } = await supabase
      .from('firm_copilot_folders')
      .update({ name: folderName.trim(), color: folderColor })
      .eq('id', editingFolder.id);

    if (!error) {
      setFolders(folders.map(f =>
        f.id === editingFolder.id ? { ...f, name: folderName.trim(), color: folderColor } : f
      ));
      toast({ title: "Folder updated" });
    }

    setEditingFolder(null);
    setFolderDialogOpen(false);
    setFolderName("");
    setFolderColor(null);
  };

  const deleteFolder = async (folderId: string) => {
    await supabase
      .from('firm_copilot_threads')
      .update({ folder_id: null })
      .eq('folder_id', folderId);

    const { error } = await supabase
      .from('firm_copilot_folders')
      .delete()
      .eq('id', folderId);

    if (!error) {
      setFolders(folders.filter(f => f.id !== folderId));
      setThreads(threads.map(t => t.folder_id === folderId ? { ...t, folder_id: null } : t));
      toast({ title: "Folder deleted" });
    }
  };

  const moveThreadToFolder = async (threadId: string, folderId: string | null) => {
    const { error } = await supabase
      .from('firm_copilot_threads')
      .update({ folder_id: folderId })
      .eq('id', threadId);

    if (!error) {
      setThreads(threads.map(t => t.id === threadId ? { ...t, folder_id: folderId } : t));
    }
  };

  const createNewThread = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || !orgId) return;

    const { data, error } = await supabase
      .from('firm_copilot_threads')
      .insert({
        org_id: orgId,
        title: 'New conversation',
        created_by: user.id
      })
      .select()
      .single();

    if (!error && data) {
      setThreads([data, ...threads]);
      setCurrentThread(data.id);
      setMessages([]);
    }
  };

  const deleteThread = async (threadId: string) => {
    const { error } = await supabase
      .from('firm_copilot_threads')
      .delete()
      .eq('id', threadId);

    if (!error) {
      setThreads(threads.filter(t => t.id !== threadId));
      if (currentThread === threadId) {
        setCurrentThread(null);
        setMessages([]);
      }
      toast({ title: "Chat deleted" });
    }
  };

  const sendMessage = async (content?: string) => {
    const messageContent = content || input;
    if (!messageContent.trim() || !orgId) return;

    let threadId = currentThread;

    if (!threadId) {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('firm_copilot_threads')
        .insert({
          org_id: orgId,
          title: generateThreadTitle(messageContent),
          created_by: user?.id
        })
        .select()
        .single();

      if (error || !data) {
        toast({ title: "Error", description: "Failed to create thread", variant: "destructive" });
        return;
      }

      threadId = data.id;
      setThreads([data, ...threads]);
      setCurrentThread(threadId);
    }

    setIsLoading(true);
    setInput("");

    // Add user message to UI
    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: messageContent,
      created_at: new Date().toISOString()
    };

    // Capture current messages for API call (before state update to avoid stale closure)
    const currentMessages = [...messages, userMessage];
    setMessages(currentMessages);

    // Save user message to DB
    const { error: insertError } = await supabase.from('firm_copilot_messages').insert({
      thread_id: threadId,
      role: 'user',
      content: messageContent
    });

    if (insertError) {
      console.error('Error saving user message:', insertError);
      toast({
        title: "Warning",
        description: "Message sent but may not be saved",
        variant: "destructive"
      });
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/firm-copilot-chat`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            threadId,
            prompt: messageContent,
            orgId,
            messages: currentMessages.map(m => ({ role: m.role, content: m.content }))
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 429) {
          throw new Error('Rate limit exceeded. Please try again in a moment.');
        }
        throw new Error(errorData.error || 'Failed to get response from AI');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let assistantContent = '';

      const assistantMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: '',
        created_at: new Date().toISOString()
      };
      setMessages(prev => [...prev, assistantMessage]);

      if (reader) {
        let buffer = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed || trimmed.startsWith(':')) continue;

            if (trimmed.startsWith('data: ')) {
              const data = trimmed.slice(6);
              if (data === '[DONE]') continue;

              try {
                const parsed = JSON.parse(data);

                // Handle tool status events
                if (parsed.type === 'tool_status') {
                  if (parsed.status === 'executing') {
                    setToolStatus(getToolLabel(parsed.tool) + '...');
                  } else {
                    setToolStatus(null);
                  }
                  continue;
                }

                const delta = parsed.choices?.[0]?.delta?.content;
                if (delta) {
                  assistantContent += delta;
                  setMessages(prev =>
                    prev.map(m =>
                      m.id === assistantMessage.id ? { ...m, content: assistantContent } : m
                    )
                  );
                }
              } catch (e) {
                // Skip parse errors
              }
            }
          }
        }
      }

      // Save assistant message
      await supabase.from('firm_copilot_messages').insert({
        thread_id: threadId,
        role: 'assistant',
        content: assistantContent
      });

      // Update thread title if first message
      if (messages.length === 0) {
        await supabase
          .from('firm_copilot_threads')
          .update({ title: generateThreadTitle(messageContent) })
          .eq('id', threadId);
        loadThreads();
      }

    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => prev.filter(m => m.role === 'user' || m.content.trim() !== ''));
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to send message",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
      setToolStatus(null);
    }
  };

  const toggleFolder = (folderId: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const openEditFolder = (folder: CopilotFolder) => {
    setEditingFolder(folder);
    setFolderName(folder.name);
    setFolderColor(folder.color);
    setFolderDialogOpen(true);
  };

  if (tenantLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col p-4 md:p-6 h-[calc(100vh-4rem)]">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Building2 className="h-6 w-6" />
          Firm Copilot
        </h1>
        <p className="text-muted-foreground">
          AI assistant with visibility across all {currentOrg?.name || 'firm'} families
        </p>
      </div>

      <div className="flex flex-1 gap-4 overflow-hidden">
        {/* Sidebar with folders and threads */}
        <Card className="w-72 flex flex-col">
          <CardContent className="p-3 flex-1 flex flex-col overflow-hidden">
            <div className="flex gap-2 mb-3">
              <Button onClick={createNewThread} className="flex-1" size="sm">
                <Plus className="h-4 w-4 mr-1" />
                New Chat
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditingFolder(null);
                  setFolderName("");
                  setFolderColor(null);
                  setFolderDialogOpen(true);
                }}
              >
                <FolderPlus className="h-4 w-4" />
              </Button>
            </div>

            <ScrollArea className="flex-1">
              {/* Folders */}
              {folders.map(folder => (
                <div key={folder.id} className="mb-1">
                  <div
                    className="flex items-center gap-1 p-2 rounded-lg hover:bg-accent cursor-pointer group"
                    onClick={() => toggleFolder(folder.id)}
                  >
                    {expandedFolders.has(folder.id) ? (
                      <ChevronDown className="h-4 w-4 shrink-0" />
                    ) : (
                      <ChevronRight className="h-4 w-4 shrink-0" />
                    )}
                    <Folder
                      className="h-4 w-4 shrink-0"
                      style={{ color: folder.color || undefined }}
                    />
                    <span className="text-sm truncate flex-1">{folder.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {groupedThreads.foldered[folder.id]?.length || 0}
                    </span>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100">
                          <MoreVertical className="h-3 w-3" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditFolder(folder)}>
                          <Pencil className="h-4 w-4" />
                          Rename
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={() => deleteFolder(folder.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {expandedFolders.has(folder.id) && (
                    <div className="ml-4 pl-2 border-l">
                      {groupedThreads.foldered[folder.id]?.map(thread => (
                        <ThreadItem
                          key={thread.id}
                          thread={thread}
                          isActive={currentThread === thread.id}
                          folders={folders}
                          onSelect={() => setCurrentThread(thread.id)}
                          onMove={moveThreadToFolder}
                          onDelete={deleteThread}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Unfiled threads */}
              {groupedThreads.unfoldered.length > 0 && (
                <div className="mt-2">
                  <div className="text-xs text-muted-foreground px-2 py-1">Unfiled</div>
                  {groupedThreads.unfoldered.map(thread => (
                    <ThreadItem
                      key={thread.id}
                      thread={thread}
                      isActive={currentThread === thread.id}
                      folders={folders}
                      onSelect={() => setCurrentThread(thread.id)}
                      onMove={moveThreadToFolder}
                      onDelete={deleteThread}
                    />
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Chat area */}
        <Card className="flex-1 flex flex-col overflow-hidden">
          <CardContent className="p-0 flex-1 flex flex-col overflow-hidden">
            {/* Context bar */}
            <div className="flex items-center justify-between gap-2 px-6 py-3 border-b bg-muted/30">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="gap-1">
                  <Building2 className="h-3 w-3" />
                  Firm View
                </Badge>
                <Badge variant="outline">{currentOrg?.name || 'Organization'}</Badge>
              </div>

              {currentThread && folders.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2">
                      <Folder className="h-4 w-4" />
                      {threads.find(t => t.id === currentThread)?.folder_id
                        ? folders.find(f => f.id === threads.find(t => t.id === currentThread)?.folder_id)?.name
                        : 'Move to folder'}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {folders.map(folder => (
                      <DropdownMenuItem
                        key={folder.id}
                        onClick={() => moveThreadToFolder(currentThread, folder.id)}
                      >
                        <Folder className="h-4 w-4" style={{ color: folder.color || undefined }} />
                        {folder.name}
                      </DropdownMenuItem>
                    ))}
                    {threads.find(t => t.id === currentThread)?.folder_id && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => moveThreadToFolder(currentThread, null)}>
                          Remove from folder
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 overflow-hidden">
              <div className="max-w-3xl mx-auto px-6 py-4">
                {messages.length === 0 && (
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground mb-4">
                      Ask about your firm's portfolio across all families:
                    </p>
                    {STARTER_QUESTIONS.map((question, idx) => (
                      <Button
                        key={idx}
                        variant="outline"
                        className="w-full text-left justify-start"
                        onClick={() => sendMessage(question)}
                      >
                        {question}
                      </Button>
                    ))}
                  </div>
                )}

                {messages.map((message) => (
                  <FirmMessageBubble key={message.id} message={message} />
                ))}

                {isLoading && !messages.some(m => m.role === 'assistant' && m.content) && (
                  <div className="flex gap-4 py-6">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 bg-muted border">
                      <Sparkles className="h-4 w-4" />
                    </div>
                    <div className="flex-1">
                      <div className="text-xs font-medium text-muted-foreground mb-1">Firm Copilot</div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm">{toolStatus || 'Thinking...'}</span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={scrollRef} />
              </div>
            </ScrollArea>

            {/* Input area */}
            <div className="border-t bg-background">
              <div className="max-w-3xl mx-auto px-6 py-4">
                <div className="flex gap-2">
                  <Input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                    placeholder="Ask about your firm's portfolio..."
                    disabled={isLoading}
                    className="flex-1"
                  />
                  <Button
                    onClick={() => sendMessage()}
                    disabled={isLoading || !input.trim()}
                    size="icon"
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Folder Dialog */}
      <Dialog open={folderDialogOpen} onOpenChange={setFolderDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingFolder ? 'Edit Folder' : 'New Folder'}</DialogTitle>
            <DialogDescription>
              {editingFolder ? 'Update the folder name and color.' : 'Create a new folder to organize your chats.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="folder-name">Name</Label>
              <Input
                id="folder-name"
                value={folderName}
                onChange={e => setFolderName(e.target.value)}
                placeholder="e.g., Q4 Analysis"
              />
            </div>
            <div>
              <Label>Color</Label>
              <div className="flex gap-2 mt-2">
                {FOLDER_COLORS.map(c => (
                  <button
                    key={c.name}
                    onClick={() => setFolderColor(c.value)}
                    className={cn(
                      "w-8 h-8 rounded-full border-2 transition-all",
                      folderColor === c.value ? "border-primary scale-110" : "border-transparent"
                    )}
                    style={{ backgroundColor: c.value || '#e5e7eb' }}
                    title={c.name}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFolderDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={editingFolder ? updateFolder : createFolder}>
              {editingFolder ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Message bubble with markdown rendering and copy button
function FirmMessageBubble({ message }: { message: Message }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [message.content]);

  return (
    <div
      className={cn(
        "flex gap-4 py-6 group/msg",
        message.role === 'user' ? "flex-row-reverse" : "flex-row"
      )}
    >
      <div className={cn(
        "w-8 h-8 rounded-full flex items-center justify-center shrink-0",
        message.role === 'user'
          ? "bg-primary text-primary-foreground"
          : "bg-muted border"
      )}>
        {message.role === 'user' ? (
          <User className="h-4 w-4" />
        ) : (
          <Sparkles className="h-4 w-4" />
        )}
      </div>

      <div className={cn(
        "flex-1 min-w-0",
        message.role === 'user' ? "text-right" : "text-left"
      )}>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-medium text-muted-foreground">
            {message.role === 'user' ? 'You' : 'Firm Copilot'}
          </span>
          {message.role === 'assistant' && message.content && (
            <button
              onClick={handleCopy}
              className="opacity-0 group-hover/msg:opacity-100 transition-opacity p-1 rounded hover:bg-muted"
              title="Copy message"
            >
              {copied ? (
                <Check className="h-3 w-3 text-green-500" />
              ) : (
                <Copy className="h-3 w-3 text-muted-foreground" />
              )}
            </button>
          )}
        </div>
        {message.role === 'assistant' ? (
          <div className="text-sm leading-relaxed prose prose-sm max-w-none dark:prose-invert break-words overflow-hidden">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {message.content}
            </ReactMarkdown>
          </div>
        ) : (
          <div className="text-sm leading-relaxed whitespace-pre-wrap break-words overflow-hidden">
            {message.content}
          </div>
        )}
      </div>
    </div>
  );
}

// Thread list item component
function ThreadItem({
  thread,
  isActive,
  folders,
  onSelect,
  onMove,
  onDelete,
}: {
  thread: Thread;
  isActive: boolean;
  folders: CopilotFolder[];
  onSelect: () => void;
  onMove: (threadId: string, folderId: string | null) => void;
  onDelete: (threadId: string) => void;
}) {
  return (
    <div
      onClick={onSelect}
      className={cn(
        "flex items-center gap-2 p-2 rounded-lg cursor-pointer group",
        isActive ? "bg-accent" : "hover:bg-accent/50"
      )}
    >
      <MessageSquare className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="text-sm truncate flex-1">{thread.title}</span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
          <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground">
            <MoreVertical className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {folders.length > 0 && (
            <>
              <DropdownMenuItem disabled className="text-xs text-muted-foreground">
                Move to folder
              </DropdownMenuItem>
              {folders.map(folder => (
                <DropdownMenuItem key={folder.id} onClick={() => onMove(thread.id, folder.id)}>
                  <Folder className="h-4 w-4" style={{ color: folder.color || undefined }} />
                  {folder.name}
                </DropdownMenuItem>
              ))}
              {thread.folder_id && (
                <DropdownMenuItem onClick={() => onMove(thread.id, null)}>
                  Remove from folder
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem className="text-destructive" onClick={() => onDelete(thread.id)}>
            <Trash2 className="h-4 w-4" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
